import { useEffect, useState } from 'react';
import i18n from '../../i18n';
import {
  getImmediateCurrentSiteLanguage,
  normalizeSiteLanguage,
  resolveCurrentSiteLanguage,
} from '../../utils/currentSiteLanguage';
import type { RouteLanguageCode } from '../../utils/languageRouting';
import { getAmplifyVisitorRegion } from '../../utils/amplifyGeo';
import DraggableChatLauncher from './DraggableChatLauncher';
import ChatMinimizeButton from './ChatMinimizeButton';

const LIVECHAT_CONFIG_FILE = 'livechat-language-groups.json';
const DEFAULT_LIVECHAT_LICENSE = 19384951;
const TRACKING_SCRIPT_ID = 'jlbhk-livechat-tracking';
const TRACKING_SCRIPT_URL = 'https://cdn.livechatinc.com/tracking.js';

type LiveChatGroupValue = number | string | null;

type LiveChatRuntimeConfig = {
  license?: number | string;
  defaultGroup?: LiveChatGroupValue;
  chatBetweenGroups?: boolean;
  draggableLauncher?: boolean;
  groups?: Partial<Record<RouteLanguageCode, LiveChatGroupValue>>;
};

type LiveChatParam = {
  name: string;
  value: string;
};

type LiveChatBootstrapConfig = {
  license?: number;
  group?: number;
  integration_name?: string;
  product_name?: string;
  chat_between_groups?: boolean;
  params?: LiveChatParam[];
  asyncInit?: boolean;
};

type LiveChatCommandHandler = (method: string, args: unknown[]) => unknown;

type LiveChatWidgetApi = {
  _q: Array<[string, unknown[]]>;
  _h: LiveChatCommandHandler | null;
  _v: string;
  on: (...args: unknown[]) => unknown;
  once: (...args: unknown[]) => unknown;
  off: (...args: unknown[]) => unknown;
  get: (...args: unknown[]) => unknown;
  call: (...args: unknown[]) => unknown;
  init: () => void;
};

type WidgetVisibility = 'maximized' | 'minimized' | 'hidden';
type WidgetAvailability = 'online' | 'offline';
type WidgetReadyPayload = {
  state?: { visibility?: WidgetVisibility; availability?: WidgetAvailability };
};
type WidgetNewEvent = { author?: { type?: string }; greeting?: unknown };

declare global {
  interface Window {
    __lc?: LiveChatBootstrapConfig;
    LiveChatWidget?: LiveChatWidgetApi;
  }
}

const LANGUAGE_LABELS: Record<RouteLanguageCode, string> = {
  hk: 'Traditional Chinese',
  cn: 'Simplified Chinese',
  en: 'English',
  jp: 'Japanese',
  kr: 'Korean',
  th: 'Thai',
  vn: 'Vietnamese',
  de: 'German',
  fr: 'French',
  it: 'Italian',
  es: 'Spanish',
  ug: 'Uyghur',
};


function resolveLiveChatLanguage() {
  const immediateLanguage = getImmediateCurrentSiteLanguage();
  if (immediateLanguage) return Promise.resolve(immediateLanguage);

  const fallbackLanguage = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const timeoutFallback = new Promise<RouteLanguageCode>((resolve) => {
    window.setTimeout(() => resolve(fallbackLanguage), 2500);
  });

  return Promise.race([resolveCurrentSiteLanguage(), timeoutFallback]);
}

function parseLicense(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : DEFAULT_LIVECHAT_LICENSE;
}

function parseGroup(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

async function loadRuntimeConfig(): Promise<LiveChatRuntimeConfig> {
  const configUrl = `${import.meta.env.BASE_URL}${LIVECHAT_CONFIG_FILE}`;

  try {
    const response = await fetch(configUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload: unknown = await response.json();
    if (!payload || typeof payload !== 'object') {
      throw new Error('Invalid JSON object');
    }

    return payload as LiveChatRuntimeConfig;
  } catch (error) {
    console.warn('[JL金利寶] LiveChat language config unavailable; using default group:', error);
    return { license: DEFAULT_LIVECHAT_LICENSE };
  }
}

function configureLiveChat(
  config: LiveChatRuntimeConfig,
  language: RouteLanguageCode,
) {
  const license = parseLicense(config.license);
  const languageGroup = parseGroup(config.groups?.[language]);
  const defaultGroup = parseGroup(config.defaultGroup);
  const selectedGroup = languageGroup ?? defaultGroup;

  const liveChatConfig: LiveChatBootstrapConfig = {
    ...(window.__lc || {}),
    license,
    integration_name: 'manual_channels',
    product_name: 'livechat',
    params: [
      { name: 'Website language code', value: language },
      { name: 'Website language', value: LANGUAGE_LABELS[language] },
      { name: 'Website path', value: window.location.pathname },
    ],
  };

  if (selectedGroup !== null) {
    liveChatConfig.group = selectedGroup;
  } else {
    delete liveChatConfig.group;
  }

  if (typeof config.chatBetweenGroups === 'boolean') {
    liveChatConfig.chat_between_groups = config.chatBetweenGroups;
  }

  window.__lc = liveChatConfig;

  return { license, selectedGroup };
}

function createLiveChatWidgetApi(): LiveChatWidgetApi {
  const api = {} as LiveChatWidgetApi;

  const dispatch = (method: string, args: unknown[]) => {
    if (api._h) return api._h(method, args);
    api._q.push([method, args]);
    return undefined;
  };

  api._q = [];
  api._h = null;
  api._v = '2.0';
  api.on = (...args) => dispatch('on', args);
  api.once = (...args) => dispatch('once', args);
  api.off = (...args) => dispatch('off', args);
  api.get = (...args) => {
    if (!api._h) {
      throw new Error("[LiveChatWidget] You can't use getters before load.");
    }

    return dispatch('get', args);
  };
  api.call = (...args) => dispatch('call', args);
  api.init = () => {
    if (document.getElementById(TRACKING_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = TRACKING_SCRIPT_ID;
    script.async = true;
    script.type = 'text/javascript';
    script.src = TRACKING_SCRIPT_URL;
    document.head.appendChild(script);
  };

  return api;
}

function initializeLiveChatWidget() {
  if (!window.LiveChatWidget) {
    window.LiveChatWidget = createLiveChatWidgetApi();
  }

  if (!window.__lc?.asyncInit) {
    window.LiveChatWidget.init();
  }
}

export default function LiveChatWidget() {
  const [launcherEnabled, setLauncherEnabled] = useState(false);
  const [launcherVisible, setLauncherVisible] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [availability, setAvailability] = useState<WidgetAvailability | null>(null);

  useEffect(() => {
    let active = true;
    let detachCallbacks: (() => void) | undefined;

    const initialize = async () => {
      // Chat support is available in Hong Kong. Only US visitors and unresolved
      // regions keep the existing chat block; other site restrictions are separate.
      const region = await getAmplifyVisitorRegion();

      if (!active) return;

      if (region.isUS || region.source === 'fallback') {
        console.info('[JL金利寶] LiveChat disabled:', {
          countryCode: region.countryCode,
          source: region.source,
          reason: region.isUS ? 'restricted-region' : 'region-unresolved',
        });
        return;
      }

      const [language, config] = await Promise.all([
        resolveLiveChatLanguage(),
        loadRuntimeConfig(),
      ]);

      if (!active) return;

      if (!window.LiveChatWidget) {
        window.LiveChatWidget = createLiveChatWidgetApi();
      }

      const { license, selectedGroup } = configureLiveChat(config, language);
      if (config.draggableLauncher === false) {
        initializeLiveChatWidget();
        return;
      }
      const api = window.LiveChatWidget;
      let ready = false;
      let visibility: WidgetVisibility = 'hidden';

      const onVisibilityChanged = (data: { visibility: WidgetVisibility }) => {
        if (!active || !ready) return;
        visibility = data.visibility;
        setChatExpanded(visibility === 'maximized');
        if (visibility === 'maximized') {
          setLauncherVisible(false);
          setUnreadCount(0);
        } else {
          // Replace only the minimized launcher, not the native conversation.
          // The hidden event does not call hide again, so there is no loop.
          if (visibility === 'minimized') api.call('hide');
          setLauncherVisible(true);
        }
      };
      const onReady = (data: WidgetReadyPayload) => {
        if (!active) return;
        ready = true;
        visibility = data.state?.visibility || 'minimized';
        setChatExpanded(visibility === 'maximized');
        setAvailability(data.state?.availability || null);
        setLauncherEnabled(true);
        if (visibility === 'maximized') {
          // Preserve a conversation restored by LiveChat across page loads.
          setLauncherVisible(false);
        } else {
          api.call('hide');
          setLauncherVisible(true);
        }
      };
      const onAvailabilityChanged = (data: { availability: WidgetAvailability }) => {
        if (active) setAvailability(data.availability);
      };
      const onNewEvent = (event: WidgetNewEvent) => {
        if (!active || !ready || visibility === 'maximized') return;
        if (event.author?.type === 'agent' || event.greeting) {
          setUnreadCount((count) => Math.min(100, count + 1));
        }
      };

      // Register before loading tracking.js; use only the documented widget API.
      api.on('ready', onReady);
      api.on('visibility_changed', onVisibilityChanged);
      api.on('availability_changed', onAvailabilityChanged);
      api.on('new_event', onNewEvent);
      detachCallbacks = () => {
        api.off('ready', onReady);
        api.off('visibility_changed', onVisibilityChanged);
        api.off('availability_changed', onAvailabilityChanged);
        api.off('new_event', onNewEvent);
      };
      initializeLiveChatWidget();

      console.info('[JL金利寶] LiveChat initialized:', {
        license,
        websiteLanguage: language,
        group: selectedGroup ?? 'LiveChat routing/fallback group',
      });
    };

    void initialize();

    return () => {
      active = false;
      detachCallbacks?.();
    };
  }, []);

  if (!launcherEnabled) return null;

  return (
    <>
      <DraggableChatLauncher
        visible={launcherVisible}
        unreadCount={unreadCount}
        availability={availability}
        onOpen={() => window.LiveChatWidget?.call('maximize')}
      />
      <ChatMinimizeButton
        visible={chatExpanded}
        onMinimize={() => window.LiveChatWidget?.call('minimize')}
      />
    </>
  );
}
